import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Logo } from "@/components/Logo";
import { useScopedPath } from "@/lib/scoped";
import { CheckCircle2, XCircle, Bell } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public page reached from the "Unsubscribe from match reminders" link at
 * the bottom of every reminder email. Hits the backend directly (no auth
 * needed — the signed token IS the auth) and shows a friendly confirmation.
 */
export default function UnsubscribePage() {
  const scope = useScopedPath();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState({ status: "loading", email: "", message: "" });

  useEffect(() => {
    if (!token) {
      setState({ status: "error", email: "", message: "Missing unsubscribe token." });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/auth/unsubscribe/reminders`, {
          params: { token },
          timeout: 15000,
        });
        if (!cancelled) {
          setState({ status: "ok", email: data.email || "", message: data.message || "" });
        }
      } catch (e) {
        if (!cancelled) {
          const detail = e?.response?.data?.detail || "This unsubscribe link is invalid or has been tampered with.";
          setState({ status: "error", email: "", message: detail });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="bg-white min-h-screen">
      <header className="border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center">
          <Logo />
        </div>
      </header>
      <main
        className="max-w-2xl mx-auto px-6 py-16"
        data-testid="unsubscribe-page"
      >
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 inline-flex items-center gap-1.5">
          <Bell className="h-3 w-3" /> Match reminders
        </p>

        {state.status === "loading" && (
          <div data-testid="unsubscribe-loading">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950 mt-2">
              Processing…
            </h1>
            <p className="text-sm text-zinc-500 mt-4">One second while we update your preferences.</p>
          </div>
        )}

        {state.status === "ok" && (
          <div data-testid="unsubscribe-success">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950 mt-2 inline-flex items-center gap-3">
              <CheckCircle2 className="h-9 w-9 text-emerald-600" /> You&apos;re unsubscribed.
            </h1>
            <p className="text-base text-zinc-600 mt-6 leading-relaxed">
              {state.message}
              {state.email && (
                <> No more reminders will be sent to <strong>{state.email}</strong>.</>
              )}
            </p>
            <div className="mt-8 flex gap-3 flex-wrap">
              <Link
                to={scope("/settings")}
                data-testid="unsubscribe-manage-link"
                className="inline-flex items-center h-11 px-6 rounded-sm bg-zinc-950 hover:bg-zinc-800 text-white font-semibold text-sm active:scale-[0.98]"
              >
                Manage in Settings
              </Link>
              <Link
                to={scope("/")}
                className="inline-flex items-center h-11 px-6 rounded-sm border border-zinc-300 hover:border-zinc-950 text-sm font-semibold"
              >
                Back to predictions
              </Link>
            </div>
          </div>
        )}

        {state.status === "error" && (
          <div data-testid="unsubscribe-error">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950 mt-2 inline-flex items-center gap-3">
              <XCircle className="h-9 w-9 text-rose-600" /> Couldn&apos;t unsubscribe.
            </h1>
            <p className="text-base text-zinc-600 mt-6 leading-relaxed">{state.message}</p>
            <p className="text-sm text-zinc-500 mt-4">
              If you keep seeing this, log in and disable reminders directly from your{" "}
              <Link to={scope("/settings")} className="underline underline-offset-4 hover:text-zinc-950">
                Settings
              </Link>{" "}
              page instead.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
